import { Duration } from 'moment';
import {
    AlertSeverity,
    AssignmentPriority,
    IClientTrackableDataObject,
    ISourceRow,
} from '../interfaces';
import { SortDirections } from '../../base';
import { SOURCES_SORT_FIELDS, SourcesSortField } from '../consts';

export const getPriorityColor = (
    priority: AlertSeverity | AssignmentPriority
): string => {
    switch (priority) {
        case AlertSeverity.WARNING:
        case AssignmentPriority.MEDIUM:
            return 'warning.main';
        case AlertSeverity.ERROR:
        case AssignmentPriority.HIGH:
            return 'error.main';
        case AlertSeverity.INFO:
        case AssignmentPriority.LOW:
        default:
            return 'action.active';
    }
};

export const parseDuration = (
    durationStr: string
): { value: number; unit: string } | null => {
    if (!durationStr) return null;
    const match = durationStr.match(/^(\d+)\s*([HhdDwmM][a-zA-Z]*)$/);
    if (!match) return null;

    const [, valueStr, unitStr] = match;
    const value = parseInt(valueStr, 10);
    if (isNaN(value)) return null;

    let unit: string;
    const lowerUnit = unitStr.toLowerCase();
    if (lowerUnit.startsWith('h')) unit = 'HR';
    else if (lowerUnit.startsWith('d')) unit = 'Days';
    else if (lowerUnit.startsWith('w')) unit = 'w';
    else if (lowerUnit.startsWith('m')) unit = 'Min';
    else return null;

    return { value, unit };
};

export const getNextPriority = (
    currentPriority: AlertSeverity
): AlertSeverity => {
    switch (currentPriority) {
        case AlertSeverity.INFO:
            return AlertSeverity.WARNING;
        case AlertSeverity.WARNING:
            return AlertSeverity.ERROR;
        case AlertSeverity.ERROR:
            return AlertSeverity.INFO;
        default:
            return AlertSeverity.INFO;
    }
};

// ===== Sources Card Utils =====

/**
 * Transforms trackable data object into an array of source rows
 */
export const transformTrackableData = (
    data: IClientTrackableDataObject,
    sortField: SourcesSortField
): Array<ISourceRow> => {
    return Object.entries(data.not_public).map(([link, stats], index) => ({
        id: link,
        link,
        rank: index + 1,
        entries: stats.entries,
        apply: stats.applications,
        percentage:
            sortField === SOURCES_SORT_FIELDS.ENTRIES
                ? stats.percentOfEntries
                : stats.percentOfApplications,
    }));
};

/**
 * Sorts source rows by the specified field and direction
 */
export const sortSourcesData = (
    data: Array<ISourceRow>,
    sortField: SourcesSortField,
    sortDirection: SortDirections | ''
): Array<ISourceRow> => {
    if (!sortDirection) {
        return data;
    }

    const sortedData = [...data].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (sortDirection === SortDirections.desc) {
            return bValue - aValue;
        } else {
            return aValue - bValue;
        }
    });

    // Update ranks after sorting
    return sortedData.map((item, index) => ({
        ...item,
        rank: index + 1,
    }));
};

/**
 * Gets the appropriate sort icon name based on current sort state
 */
export const getSortIconName = (
    currentField: SourcesSortField,
    targetField: SourcesSortField,
    sortDirection: SortDirections | ''
): string => {
    if (currentField === targetField && sortDirection) {
        return sortDirection === SortDirections.asc
            ? 'ExpandLessOutlinedIcon'
            : 'ExpandMoreOutlinedIcon';
    }
    return 'UnfoldMoreIcon';
};

/**
 * Calculates the next sort direction when toggling sort
 */
export const getNextSortDirection = (
    currentDirection: SortDirections | ''
): SortDirections => {
    if (currentDirection === SortDirections.desc) {
        return SortDirections.asc;
    }
    return SortDirections.desc;
};
